/**
 * API client do calendário forense (área admin): as regras oficiais de feriado, recesso e suspensão,
 * e a conferência — o que não conta num lugar e a conta de um prazo. Ver docs/PLANO_CONTAGEM_PRAZOS.md (Bloco D).
 */
import { api } from './client';

export type TipoRegra = 'feriado' | 'recesso' | 'suspensao';
export type Recorrencia = 'anual' | 'pascoa' | 'periodo_anual' | 'unica';
export type Abrangencia = 'nacional' | 'estadual' | 'municipal' | 'tribunal' | 'tribunal_2grau' | 'unidade';
export type Justica =
  'estadual' | 'federal' | 'trabalho' | 'eleitoral' | 'militar_uniao' | 'militar_estadual' | 'superior';

/** Exceção de um ano: não há (`null`), transferido (data ISO) ou `{para, ate?, manter_original?}`. */
export type Excecao = null | string | { para: string; ate?: string; manter_original?: boolean };

export interface RegraForenseInput {
  nome: string;
  tipo: TipoRegra;
  recorrencia: Recorrencia;
  data: string;
  data_fim: string | null;
  offset_pascoa: number | null;
  excecoes: Record<string, Excecao> | null;
  abrangencia: Abrangencia;
  justicas: Justica[] | null;
  uf: string | null;
  municipio_ibge: number | null;
  municipio_nome: string | null;
  tribunal: string | null;
  orgao: string | null;
  ato_normativo: string;
  observacao: string | null;
}

export interface RegraForense extends RegraForenseInput {
  id: string;
  origem: string;
  ativo: boolean;
  updated_at: string;
  /** Próximas datas em que a regra cai (início de cada ocorrência). */
  proximas: string[];
}

/** O lugar do ato. Vazio = sem filtro (na conferência, só o que vale no país inteiro). */
export interface LugarFiltro {
  justica?: Justica;
  tribunal?: string;
  uf?: string;
  municipio_ibge?: number;
  orgao?: string;
  segundo_grau?: boolean;
}

export interface DiaQueNaoConta {
  data: string;
  efeito: 'feriado' | 'suspensao';
  motivos: string[];
}

export interface ContarInput {
  data: string;
  dias: number;
  forma: 'djen' | 'dje';
  regime: 'uteis' | 'corridos';
  em_dobro: boolean;
}

export interface Contagem {
  publicacao: string | null;
  inicio: string;
  vencimento: string;
  dias_efetivos: number;
  passos: string[];
  calendario_versao: string;
}

const BASE = '/admin/calendario-forense';

/** Tira os campos vazios: o backend lê "ausente" como "sem filtro". */
function parametros(lugar: LugarFiltro): Record<string, string | number | boolean> {
  const p: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(lugar)) {
    if (v === undefined || v === '' || v === false) continue;
    p[k] = v;
  }
  return p;
}

export async function fetchRegrasForenses(lugar: LugarFiltro, incluirInativas: boolean): Promise<RegraForense[]> {
  const { data } = await api.get<RegraForense[]>(`${BASE}/regras`, {
    params: { ...parametros(lugar), ...(incluirInativas ? { incluir_inativas: true } : {}) },
  });
  return data;
}

export async function criarRegraForense(input: RegraForenseInput): Promise<RegraForense> {
  const { data } = await api.post<RegraForense>(`${BASE}/regras`, input);
  return data;
}

export async function atualizarRegraForense(id: string, input: RegraForenseInput): Promise<RegraForense> {
  const { data } = await api.put<RegraForense>(`${BASE}/regras/${encodeURIComponent(id)}`, input);
  return data;
}

export async function definirRegraAtiva(id: string, ativo: boolean): Promise<RegraForense> {
  const { data } = await api.put<RegraForense>(`${BASE}/regras/${encodeURIComponent(id)}/ativo`, { ativo });
  return data;
}

export interface SugestaoForense extends RegraForense {
  sugestao: 'pendente' | 'promovida' | 'recusada' | null;
  sugestao_nota: string | null;
  regra_oficial_id: string | null;
  created_at: string;
  advogado_nome: string | null;
  advogado_email: string | null;
}

export async function fetchSugestoesForenses(
  situacao: 'pendente' | 'promovida' | 'recusada',
): Promise<SugestaoForense[]> {
  const { data } = await api.get<SugestaoForense[]>(`${BASE}/sugestoes`, { params: { situacao } });
  return data;
}

export async function promoverSugestao(id: string, input: RegraForenseInput): Promise<RegraForense> {
  const { data } = await api.post<RegraForense>(`${BASE}/sugestoes/${encodeURIComponent(id)}/promover`, input);
  return data;
}

export async function recusarSugestao(id: string, nota: string | null): Promise<SugestaoForense> {
  const { data } = await api.post<SugestaoForense>(`${BASE}/sugestoes/${encodeURIComponent(id)}/recusar`, { nota });
  return data;
}

export async function fetchDiasQueNaoContam(lugar: LugarFiltro, de: string, ate: string): Promise<DiaQueNaoConta[]> {
  const { data } = await api.get<DiaQueNaoConta[]>(`${BASE}/dias-que-nao-contam`, {
    params: { ...parametros(lugar), de, ate },
  });
  return data;
}

export async function contarPrazo(lugar: LugarFiltro, input: ContarInput): Promise<Contagem> {
  const { data } = await api.get<Contagem>(`${BASE}/contar`, {
    params: { ...parametros(lugar), ...input, em_dobro: input.em_dobro },
  });
  return data;
}
